import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Star, Sparkles, PartyPopper, Check } from "lucide-react";
import { MaskedLines, Reveal, SectionHead, EASE } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { SERVICES, IMG, TAGLINE, waLink, DEFAULT_WA_TEXT } from "@/lib/data";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  return (
    <section ref={ref} className="relative flex min-h-screen items-center overflow-hidden pb-20 pt-32" data-testid="hero-section">
      <div className="brand-glow pointer-events-none absolute inset-0" />
      <motion.div style={{ y: yImg }} className="spotlight-beam pointer-events-none absolute inset-0" />
      <div className="confetti-dots pointer-events-none absolute inset-0 opacity-70" />

      <Star size={22} className="animate-float absolute left-[8%] top-[22%] text-gold-light/70" style={{ animationDelay: "0s" }} />
      <Star size={16} className="animate-float absolute left-[45%] top-[14%] text-white/70" style={{ animationDelay: "1.5s" }} />
      <Star size={26} className="animate-float absolute right-[6%] bottom-[18%] hidden text-gold-light/60 lg:block" style={{ animationDelay: "3s" }} />

      <div className="relative mx-auto grid w-full max-w-7xl items-center gap-14 px-6 lg:grid-cols-[1.05fr_0.95fr]">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.35em] text-gold-light"
            data-testid="hero-eyebrow"
          >
            <Sparkles size={14} />
            Kinderfeesten · Huwelijken · Communies & meer
          </motion.p>

          <MaskedLines
            as="h1"
            delay={0.3}
            lines={[
              "Feestje?",
              <>
                <em className="gold-text font-display italic">Wij toveren</em> 't om!
              </>,
            ]}
            className="mt-6 block font-display text-6xl font-black leading-[1.02] tracking-tight text-snow sm:text-7xl lg:text-8xl"
          />

          <motion.div
            initial={{ opacity: 0, rotate: -3 }}
            animate={{ opacity: 1, rotate: -2 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-4"
          >
            <p className="font-script text-3xl text-gold-light" data-testid="hero-tagline">{TAGLINE}</p>
            <svg viewBox="0 0 220 12" className="mt-1 w-52" fill="none" aria-hidden="true">
              <path d="M3 9 C 60 2, 160 2, 217 6" stroke="#FFC93C" strokeWidth="3" strokeLinecap="round" />
            </svg>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
            className="mt-6 max-w-lg text-lg font-semibold leading-relaxed text-blush"
            data-testid="hero-subline"
          >
            Van dolle kinderfeestjes tot romantische huwelijken en knallende communies:
            goochelshows, circuskunst en workshops waar iedereen van juicht. Heel België & Nederland!
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-offerte"
              className="joy-hover flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-black uppercase tracking-wider text-brand-dark"
            >
              Plan jouw feestje!
              <ArrowRight size={16} />
            </Link>
            <a
              href={waLink(DEFAULT_WA_TEXT)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="joy-hover flex items-center gap-2 rounded-full border-2 border-white/70 px-8 py-4 text-sm font-extrabold uppercase tracking-wider text-white hover:bg-white hover:text-brand"
            >
              App ons direct!
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.35 }}
            className="mt-11 flex flex-wrap gap-x-8 gap-y-3 text-sm font-bold text-blush"
          >
            {["Kinderfeesten & communies", "Huwelijken & jubilea", "Ook voor bedrijven"].map((c) => (
              <span key={c} className="flex items-center gap-2">
                <Star size={10} className="fill-gold-light text-gold-light" />
                {c}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div style={{ y: yImg }} className="relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
            className="relative rotate-1 overflow-hidden rounded-[2rem] border-4 border-white/80 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.45)] transition-transform duration-500 hover:rotate-0"
            data-testid="hero-image-frame"
          >
            <img
              src={IMG.hero}
              alt="Circusartiest op een eenwieler bij een familiefeest"
              className="h-[540px] w-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/80 via-transparent to-transparent" />
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-7">
              <div>
                <p className="font-display text-xl font-extrabold text-white">De ster van het feest?</p>
                <p className="font-semibold text-gold-light">Dat ben jij!</p>
              </div>
              <span className="rounded-full border-2 border-gold-light/70 bg-brand/60 px-4 py-2 text-xs font-extrabold uppercase tracking-widest text-gold-light backdrop-blur">
                Live
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, rotate: -30, scale: 0.6 }}
            animate={{ opacity: 1, rotate: 0, scale: 1 }}
            transition={{ duration: 1, delay: 1.25, ease: EASE }}
            className="absolute -left-10 -top-8 flex h-28 w-28 animate-spin-slow items-center justify-center rounded-full border-2 border-gold-light/70 bg-white/90 backdrop-blur"
          >
            <Star size={30} className="fill-gold text-gold" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const BentoCard = ({ service, tall = false }) => (
  <Link
    to={`/diensten/${service.slug}`}
    data-testid={`bento-card-${service.slug}`}
    className={`card-glow group relative block overflow-hidden rounded-3xl border-2 border-gold/50 bg-white ${
      tall ? "md:col-span-2 md:row-span-2 min-h-[420px] md:min-h-[560px]" : "min-h-[260px]"
    } transition-transform duration-500 hover:-rotate-1 hover:scale-[1.01]`}
  >
    <img
      src={service.image}
      alt={service.title}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover opacity-80 transition-all duration-700 group-hover:scale-105 group-hover:opacity-95"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/35 to-transparent" />
    <div className="relative flex h-full flex-col justify-end p-7">
      <span className="text-xs font-extrabold uppercase tracking-[0.25em] text-gold-light">{service.tag}</span>
      <h3 className={`mt-2 font-display font-black text-white ${tall ? "text-3xl lg:text-4xl" : "text-2xl"}`}>
        {service.title}
      </h3>
      <p className="mt-2 max-w-md text-sm font-semibold leading-relaxed text-blush">
        {service.tagline}
      </p>
      <span className="mt-4 flex items-center gap-2 text-sm font-black text-gold-light">
        Bekijk {service.title.toLowerCase()}
        <span className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-gold-light transition-all duration-300 group-hover:bg-gold group-hover:text-brand-dark">
          <ArrowUpRight size={14} />
        </span>
      </span>
    </div>
  </Link>
);

const Diensten = () => (
  <section className="relative bg-cream py-24 lg:py-32" data-testid="section-diensten">
    <div className="mx-auto max-w-7xl px-6">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHead
          eyebrow="Ons aanbod"
          lines={[<>Vier disciplines, <em className="gold-text italic">één</em> wow-gevoel!</>]}
          sub="Elk feest is anders! Kies één discipline of combineer alles tot een avondvullend programma."
        />
        <Reveal delay={0.2}>
          <Link
            to="/aanbod"
            data-testid="diensten-link-aanbod"
            className="joy-hover flex items-center gap-2 rounded-full border-2 border-brand/40 px-6 py-3 text-sm font-extrabold text-brand hover:bg-brand hover:text-white"
          >
            Alles bekijken
            <ArrowRight size={15} />
          </Link>
        </Reveal>
      </div>

      <div className="mt-14 grid gap-5 md:grid-cols-3">
        <BentoCard service={SERVICES[0]} tall />
        <BentoCard service={SERVICES[1]} />
        <BentoCard service={SERVICES[2]} />
        <BentoCard service={SERVICES[3]} />
      </div>
    </div>
  </section>
);

const Audience = () => (
  <section className="relative bg-brand-deep py-24 lg:py-32" data-testid="section-audience">
    <div className="confetti-dots pointer-events-none absolute inset-0 opacity-40" />
    <div className="relative mx-auto max-w-7xl px-6">
      <SectionHead
        eyebrow="Voor wie?"
        lines={[<>Kinderfeesten & <em className="gold-text italic">privéfeesten</em>!</>]}
        sub="Verjaardagen, huwelijken, communies of gewoon zomaar een feestje: wij maken van jouw dag het gesprek van het jaar, voor jong en oud!"
        center
        dark
      />

      <div className="mt-12 grid items-center gap-10 lg:grid-cols-2" data-testid="audience-panel-particulier">
        <Reveal>
          <div className="relative -rotate-1 overflow-hidden rounded-3xl border-4 border-white/70 transition-transform duration-500 hover:rotate-0">
            <img src={IMG.workshopBalloons} alt="Feestelijke ballonnen in alle kleuren" loading="lazy" className="h-80 w-full object-cover lg:h-[420px]" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/85 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 flex items-center gap-3">
              <PartyPopper size={22} className="text-gold-light" />
              <p className="font-display text-2xl font-extrabold text-white">Kinderfeesten & privéfeesten</p>
            </div>
          </div>
        </Reveal>
        <div>
          <ul className="space-y-4">
            {[
              "Verjaardagen, kinder- en familiefeesten!",
              "Huwelijken, communies en jubilea!",
              "Interactieve shows met de jarige als ster!",
              "Workshops waarin iedereen meedoet!",
            ].map((b, i) => (
              <Reveal key={b} delay={0.1 + i * 0.08}>
                <li className="flex items-start gap-3 font-bold text-snow">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold text-brand-dark">
                    <Check size={13} />
                  </span>
                  {b}
                </li>
              </Reveal>
            ))}
          </ul>
          <Reveal delay={0.45}>
            <Link
              to="/contact"
              data-testid="audience-cta-particulier"
              className="joy-hover mt-9 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-black uppercase tracking-wider text-brand-dark"
            >
              Offerte op maat!
              <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const CtaBanner = () => (
  <section className="bg-cream px-6 pb-24 pt-4" data-testid="section-cta">
    <Reveal>
      <div className="relative mx-auto max-w-6xl overflow-hidden rounded-[2.5rem] border-4 border-gold bg-brand-deep px-8 py-20 text-center">
        <div className="spotlight-beam pointer-events-none absolute inset-0" />
        <div className="confetti-dots pointer-events-none absolute inset-0 opacity-60" />
        <div className="relative">
          <p className="font-script text-3xl text-gold-light">Hiep hiep hoera!</p>
          <MaskedLines
            scroll
            lines={["Klaar voor het", <><em className="gold-text italic">leukste feest</em> ooit?</>]}
            className="mt-4 block font-display text-4xl font-black text-snow sm:text-5xl lg:text-6xl"
          />
          <p className="mx-auto mt-6 max-w-md font-semibold text-blush">
            Weekendjes zijn snel volgeboekt, zeker in het communieseizoen!
            Vraag vandaag je vrijblijvende offerte aan.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              data-testid="cta-banner-book"
              className="joy-hover flex items-center gap-2 rounded-full bg-gold px-9 py-4 text-sm font-black uppercase tracking-wider text-brand-dark"
            >
              Ja, plan mijn feest!
              <ArrowRight size={16} />
            </Link>
            <a
              href={waLink(DEFAULT_WA_TEXT)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-banner-whatsapp"
              className="joy-hover rounded-full border-2 border-white/60 px-9 py-4 text-sm font-extrabold uppercase tracking-wider text-white hover:bg-white hover:text-brand"
            >
              WhatsApp ons
            </a>
          </div>
        </div>
      </div>
    </Reveal>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="Goochelshows & Circusshows huren | entertainmentshows.be"
        description="Boek een goochelshow, tafelgoochelaar, circusartiest of circusworkshop voor je kinderfeest, huwelijk, communie of bedrijf. Heel België & Nederland!"
        path="/"
      />
      <Hero />
      <Diensten />
      <Audience />
      <CtaBanner />
    </>
  );
}
